import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { Store } from "@ngrx/store";
import { saveAs } from "file-saver";
import { get } from "lodash";
import { Observable } from "rxjs";
import { MdDialog } from "@angular/material";
import { LDClient } from "launchdarkly-js-client-sdk";
import * as LogRocket from "logrocket";

import { environment } from "../environments/environment";
import { ConfirmationDialogComponent } from "./confirmation-dialog/confirmation-dialog.component";
import { STATUS } from "./shared/constant";
import {
  AuthService,
  CampusService,
  ParticipationFormService,
  PrintService,
} from "./shared/service";
import { RSS_AUTH_CLIENT_URL } from "./config/auth-config";
import { LaunchDarklyService } from "./launchdarkly.service";

const ROLES = require("../../../server/shared/constants/role.constant");
export interface Campus {
  code: string;
  name: string;
  uri: string;
}

export interface AuthUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  campus: Campus;
  rss: boolean;
}

export interface CustomWindow extends Window {
  BrowserSecurity: any;
}
declare const window: CustomWindow;
declare const aptrinsic: any;

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  encapsulation: ViewEncapsulation.None,
  styleUrls: ["./app.component.scss"],
})
export class AppComponent implements OnInit {
  config: any = environment;
  profile$: Observable<any>;
  ldClient: LDClient;
  token: any;
  idClaim: any;
  browserSecurity: any;
  authUser: AuthUser;
  profileUser: any;

  constructor(
    private store: Store<any>,
    private dialog: MdDialog,
    private printService: PrintService,
    private participationFormService: ParticipationFormService,
    private launchDarklyService: LaunchDarklyService,
    private authService: AuthService,
    private campusService: CampusService,
  ) {
    this.ldClient = this.launchDarklyService.getLDClient();
    LogRocket.init("rss/platform");
    const defaultLocationUtils = {
      assign: (url: string) => window.location.assign(url),
      get href() {
        return window.location.href;
      },
    };
    this.browserSecurity = new window.BrowserSecurity(
      defaultLocationUtils,
      RSS_AUTH_CLIENT_URL,
    );
  }

  async fetchTenants() {
    try {
      const tenants = await this.campusService.fetchTenants();
      return tenants;
    } catch (err) {
      return [];
    }
  }

  isUcopAdmin(currentUser) {
    return currentUser && currentUser.role === ROLES.ROLE_UCOP_ADMIN;
  }

  isAppAdmin(currentUser) {
    return currentUser && currentUser.role === ROLES.ROLE_APP_ADMIN;
  }

  canManageStuckPrograms(currentUser) {
    return this.isAppAdmin(currentUser) || this.isUcopAdmin(currentUser);
  }

  renderKiwiNavbar() {
    const navbar: any = document.querySelector("rss-nav-bar");
    if (navbar) {
      // const isAdmin = this.isUcopAdmin(this.profileUser);
      navbar.setAttribute("ld-client-id", this.config.LD_CLIENT_ID);
      this.fetchTenants().then((tenantsResponse: any) => {
        const tenantWithCampus = tenantsResponse?.data?.tenants?.find((t) =>
          t.campusList.find(
            (c) => c.campusCode === this.authUser?.campus?.code,
          ),
        );
        const campusRes = tenantWithCampus?.campusList?.find(
          (campus) => campus.campusCode === this.authUser?.campus?.code,
        );

        const impersonateId = this.authService.getSpoofUser();

        const appLinks = [
          { name: "Home", href: "/slipresistant" },
          { name: "Order / Track", href: "/tracker" },
          {
            name: "Status",
            href: "/slipresistant/participationFormList",
          },
        ];
        // if (isAdmin) {
        // TODO: Need to push the "Export Employee Payroll Titles" to appLinks.
        // Currently web component does not support onClick functionality
        // }
        navbar.props = {
          appContext: {
            name: "Slip Resistant",
            appLinks,
          },
          user: {
            id: this.authUser.id,
            key: this.authUser.id,
            firstName: this.authUser.firstName,
            lastName: this.authUser.lastName,
            email: this.authUser.email,
            rss: this.authService.hasImpersonateAccess(this.authUser),
            campus: {
              name:
                campusRes?.displayName ||
                campusRes?.shortName ||
                campusRes?.name,
              code: this.authUser.campus.code,
              uri: campusRes?.uri,
            },
            isSpoofed: impersonateId ? Boolean(impersonateId?.userId) : false,
          },
          logout: () => this.signout(),
        };
      });
    }
  }

  ngOnInit() {
    if (window.location.hostname.match(/localhost/i)) {
      document.getElementById("common-css")["href"] =
        `${environment.BRAND_URL}/css/rss-common.css`;
    }
    this.signin();
    try {
      this.profile$ = this.store
        .select("PERSON_REDUCER")
        .filter((state) => state["status"] === STATUS.COMPLETED);

      this.profile$.subscribe(({ user }) => {
        this.profileUser = user;
        this.handleProfileUserChange();
      });

      this.ldClient.on("ready", () => {
        this.handleProfileUserChange();
        this.renderKiwiNavbar();
      });

      this.ldClient.on("change", () => this.handleProfileUserChange());
    } catch (err) {
      this.signin();
    }
  }

  handleProfileUserChange() {
    if (this.profileUser) {
      const { firstName, lastName, email, campusCode }: any =
        this.profileUser || {};

      if (this.ldClient) {
        this.ldClient.identify({
          key: "initial",
          firstName: firstName,
          lastName: lastName,
          email: email,
          secondary: campusCode,
          custom: {
            campus: campusCode,
          },
        });
      }

      if (window["aptrinsic"]) {
        // For Gainsight
        window["aptrinsic"](
          "identify",
          {
            // User Fields
            id: this.profileUser?.userId,
            firstName: firstName,
            lastName: lastName,
            email: email,
          },
          {
            // Account Fields
            id: campusCode,
            name: this.authUser.campus.name,
          },
        );
      }

      LogRocket.identify(this.profileUser.userId, {
        name: `${firstName} ${lastName}`,
        email: email,
        campus: campusCode,
      });
    }
  }

  signin() {
    if (!this.browserSecurity.isAuthenticated) {
      this.browserSecurity.login();
    } else {
      this.authUser = this.browserSecurity.user;
    }
  }

  signout() {
    this.browserSecurity.logout("/auth/logout");
  }

  getLink(): string {
    return environment.GROUP_TRACKABLES_URL;
  }

  exportEmployeePayrollTitles() {
    this.participationFormService.exportEmployeePayrollTitles().then((data) => {
      const filename = `Slip-resistant_Employee Payroll Titles_${new Date()
        .toLocaleDateString()
        .replace(/\//g, "_")}.xlsx`;

      this.printService
        .printXlsx({
          filename,
          data: [
            {
              name: "All Employee",
              data: get(data, "employeePayrollTitles"),
            },
            {
              name: "Unique Payroll Title",
              data: get(data, "uniquePayrollTitles"),
            },
          ],
        })
        .then((blob) => saveAs(blob, filename));
    });
  }

  openDialog(sideMenu) {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      width: "600px",
    });

    dialogRef.afterClosed().subscribe(() => {
      sideMenu.close();
    });
  }
}



// WEBPACK FOOTER //
// /home/runner/work/slipresistant/slipresistant/client/src/app/app.component.ts